import * as Sentry from "@sentry/react";

import { ThemeProvider } from "next-themes";
import { Fragment, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";

import { usePostHog } from "@posthog/react";
import { Toaster } from "@/components/ui/sonner";
import { Loader } from "@/components/layout/loader";
import { ErrorLayout } from "@/components/layout/error";

import { useAuthenticationStore } from "@/store/authentication";
import { useSubscriptionStore } from "@/store/subscription";
import { useInitializeSession } from "@/hooks/use-initialize";

import { AuthApiFactory } from "@/apis/auth";
import { KnowledgeApiFactory } from "@/apis/knowledge";

import { RouterContext } from "@/types/common";
import { version } from "@/config/version";
import { extractAuthentication, removeAuthentication } from "@/lib/auth";

import { InjectMetaPixel } from "@/scipts/meta";
import { InjectTiktokPixel } from "@/scipts/tiktok";
import { InjectGtag } from "@/scipts/gtag";
import { InjectTwitterPixel } from "@/scipts/twitter";
import { InjectGoogleTagManager } from "@/scipts/google-tag-manager";

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

function RootComponent() {
  const [isInitialized, setInitialized] = useState(false);

  const posthog = usePostHog();
  const client = useQueryClient();
  const user = useAuthenticationStore((store) => store.user);

  const initializeSession = useInitializeSession();
  const authenticate = useAuthenticationStore((store) => store.authenticate);
  const setSubscription = useSubscriptionStore((store) => store.setSubscription);
  const reauthenticate = useAuthenticationStore((store) => store.reauthenticate);

  useEffect(() => {
    const tokens = extractAuthentication();
    if (tokens.accessToken || tokens.refreshToken) {
      AuthApiFactory.Apis.Authenticate()
        .then(async (session) => {
          const initialize = await initializeSession();
          authenticate(session.data.user, session.data.organization, initialize.profile);
          client.prefetchQuery(KnowledgeApiFactory.Queries.FetchBrandProducts({ page: 1, size: 20 }));
          setSubscription(initialize.subscription);
          setInitialized(true);
        })
        .catch(() => {
          reauthenticate();
          removeAuthentication();
          setInitialized(true);
        });
    } else {
      reauthenticate();
      setSubscription(null);
      setInitialized(true);
    }
  }, [client, reauthenticate, authenticate, setSubscription, initializeSession]);

  useEffect(() => {
    if (!user?.id || !user?.email || !posthog) {
      return;
    }
    posthog.identify(user.id, {
      email: user.email,
    });
  }, [user?.id, user?.email, posthog]);

  useEffect(() => {
    if (!user?.id || !user?.email) {
      return;
    }
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  }, [user?.id, user?.email, user?.name]);

  useEffect(() => {
    console.log("Version:", version);
  }, []);

  if (!isInitialized) {
    return (
      <Fragment>
        <Loader />
      </Fragment>
    );
  }

  return (
    <ErrorLayout>
      <ThemeProvider defaultTheme="light" attribute="class">
        <Outlet />
        <Toaster richColors />
      </ThemeProvider>
      <InjectMetaPixel email={user?.email || ""} />
      <InjectTiktokPixel />
      <InjectTwitterPixel />
      <InjectGtag />
      <InjectGoogleTagManager />
    </ErrorLayout>
  );
}
